// src/components/LandingPage/Templates.js
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useGetTemplates } from '../../../services/api/subscription-api';

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const TemplatesSection = styled.section`
  padding: 5rem 4rem;
  text-align: center;
  overflow-x: hidden;

  h3 {
    font-size: 2.4rem;
    font-weight: 800;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.02em;
  }

  > p {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.text}88;
    margin-bottom: 4rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .template-container {
    overflow: hidden;
    position: relative;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
  }

  .template-track {
    display: flex;
    gap: 2rem;
    animation: ${scroll} 30s linear infinite;
    padding: 1rem 0;

    &:hover {
      animation-play-state: paused;
    }
  }

  .template-card {
    min-width: 280px;
    background: ${({ theme }) => theme.cardBackground};
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    border: 1px solid ${({ theme }) => `${theme.text}08`};
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 12px 40px rgba(88, 166, 255, 0.15);
      border-color: #58a6ff33;
    }

    .template-media {
      position: relative;
      height: 220px;
      overflow: hidden;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      border-bottom: 1px solid ${({ theme }) => `${theme.text}08`};

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(15,23,42,0.12) 100%);
        pointer-events: none;
      }
    }

    .template-image {
      width: 100%;
      height: 200px;
      object-fit: cover;
      display: block;
    }

    .template-frame {
      width: 100%;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 0.95rem;
      text-align: center;
      padding: 1rem;
      position: relative;
      overflow: hidden;
      border: 1px solid rgba(255,255,255,0.12);
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.06);

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at top right, rgba(255,255,255,0.1), transparent);
        pointer-events: none;
      }

      i {
        font-size: 2rem;
        margin-bottom: 0.5rem;
        position: relative;
        z-index: 1;
      }

      p {
        position: relative;
        z-index: 1;
        max-width: 80%;
        margin: 0 auto;
        line-height: 1.5;
      }
    }

    .template-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 0.75rem;
      color: white;
      text-align: center;
      padding: 1.5rem;
      position: relative;
      z-index: 1;

      .placeholder-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.45rem 0.8rem;
        border-radius: 999px;
        background: rgba(255,255,255,0.14);
        backdrop-filter: blur(8px);
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }

      .placeholder-title {
        font-size: 1.1rem;
        font-weight: 800;
      }

      .placeholder-text {
        font-size: 0.9rem;
        max-width: 220px;
        opacity: 0.92;
        line-height: 1.5;
      }
    }

    .template-info {
      padding: 1.5rem;
      font-size: 1rem;

      h4 {
        margin-bottom: 0.5rem;
        font-size: 1.1rem;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
      }

      p {
        font-size: 0.95rem;
        color: ${({ theme }) => theme.text}88;
        margin-top: 0.8rem;
        line-height: 1.5;
      }

      .price {
        font-weight: bold;
        color: #58a6ff;
        margin-top: 0.8rem;
      }
    }
  }

  .loading-state {
    text-align: center;
    padding: 3rem;
    color: ${({ theme }) => theme.text}88;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    padding: 3rem 2rem;
    
    h3 {
      font-size: 2rem;
    }

    .template-card {
      min-width: 250px;
    }
  }
`;



// Gradient colors for template preview
const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
];

// Local templates (from public folder and static data)
const LOCAL_TEMPLATES = [
  {
    id: 'sabri-template',
    name: 'Sabri Template',
    description: 'Modern and sleek design',
    price: 0,
    image: '/sabri-template.png',
    isLocal: true,
  },
];

const normalizeTemplates = (responseData) => {
  if (Array.isArray(responseData?.results)) return responseData.results;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const getTemplateImage = (template) => {
  return template?.image || template?.image_url || template?.preview_image || template?.thumbnail || '';
};

const shouldShowImage = (template) => Boolean(getTemplateImage(template));

const Templates = () => {
  const { data: apiResponse, isLoading } = useGetTemplates();
  
  // Extract templates from API response
  const apiTemplates = useMemo(() => {
    return normalizeTemplates(apiResponse?.data);
  }, [apiResponse]);

  // Combine local and API templates
  const allTemplates = useMemo(() => {
    const combined = [...LOCAL_TEMPLATES, ...apiTemplates];
    return combined.length > 1 ? [...combined, ...combined] : combined;
  }, [apiTemplates]);

  // Format price
  const formatPrice = (price) => {
    if (price === 0 || price === '0') return 'Free';
    return `$${parseFloat(price).toFixed(2)}`;
  };

  // Get gradient for template (cycle through gradients)
  const getGradient = (index) => {
    return gradients[index % gradients.length];
  };

  if (isLoading) {
    return (
      <TemplatesSection id="templates">
        <h3>Professional Templates</h3>
        <p>Choose from beautiful, industry-specific designs that you can customize in minutes</p>
        <div className="loading-state">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}></i>
          Loading templates...
        </div>
      </TemplatesSection>
    );
  }

  return (
    <TemplatesSection id="templates">
      <h3>Professional Templates</h3>
      <p>Choose from beautiful, industry-specific designs that you can customize in minutes</p>
      <div className="template-container">
        <div className="template-track">
          {allTemplates.map(({ id, name, description, image, price }, index) => (
            <div className="template-card" key={`${id}-${index}`}>
              {shouldShowImage({ image }) ? (
                <div className="template-media">
                  <img className="template-image" src={image} alt={name} />
                </div>
              ) : (
                <div
                  className="template-media template-frame"
                  style={{ background: getGradient(index) }}
                >
                  <div className="template-placeholder">
                    <span className="placeholder-badge">
                      <i className="pi pi-image" />
                      Preview coming soon
                    </span>
                    <div className="placeholder-title">{name}</div>
                    <div className="placeholder-text">
                      {description || 'A new template preview will appear here.'}
                    </div>
                  </div>
                </div>
              )}
              <div className="template-info">
                <h4>{name}</h4>
                <p>{description}</p>
                <div className="price">{formatPrice(price)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </TemplatesSection>
  );
};

export default Templates;
